import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { TournamentTitle } from "./tournament_title.js";

export abstract class AbstractTournamentDialog extends AbstractDialog
{
  protected static readonly BG_W = 990;
  private static readonly PART_Y = 13;
  private static readonly TOP_SUBSTRATE_W = 838;
  private static readonly TOP_SUBSTRATE_H = 280;
  private static readonly TOP_SUBSTRATE_Y = -100;
  private static readonly TITLE_CENTER_Y = AbstractTournamentDialog.TOP_SUBSTRATE_Y + 112;
  protected static readonly CORNER_BTN_SIZE = 110;
  protected static readonly CORNER_BTN_OVERHANG_X = 9;
  protected static readonly CORNER_BTN_Y = -28;
  private static readonly CLOSE_ART_SIZE = 110;
  private static readonly CLOSE_ART_PAD = 3;
  private static readonly PAD_X = 24;
  private static readonly PAD_Y = 38;

  private close_btn!: UIImage;
  private readonly bg_h: number;
  private readonly title_offset_y: number;

  constructor( bg_h: number, title_offset_y: number = 0 )
  {
    const body_w = AbstractTournamentDialog.bodyWidth();
    const body_h = AbstractTournamentDialog.bodyHeight( bg_h );

    super( DialogManager.ctx, AbstractTournamentDialog.bodyX(), AbstractTournamentDialog.bodyY( bg_h ), body_w, body_h, true );

    this.bg_h = bg_h;
    this.title_offset_y = title_offset_y;

    this.createBackground();
    this.createBackgroundPart();
    this.createTopSubstrate();
    this.createTitle();
    this.createCloseButton();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  private static bodyWidth(): number
  {
    return LayoutScaler.widthFromDesign( AbstractTournamentDialog.BG_W + AbstractTournamentDialog.PAD_X * 2 );
  }

  private static bodyHeight( bg_h: number ): number
  {
    return bg_h + LayoutScaler.heightFromDesign( AbstractTournamentDialog.PAD_Y ) * 2;
  }

  private static bodyX(): number
  {
    return ( DialogManager.getWidth() - AbstractTournamentDialog.bodyWidth() ) / 2;
  }

  private static bodyY( bg_h: number ): number
  {
    return ( DialogManager.getHeight() - AbstractTournamentDialog.bodyHeight( bg_h ) ) / 2;
  }

  private titlePos(): Vector2
  {
    return new Vector2(
      this.bgCenterX( TournamentTitle.TITLE_W ),
      this.bgY( AbstractTournamentDialog.TITLE_CENTER_Y - TournamentTitle.TITLE_H / 2 + this.title_offset_y )
    );
  }

  protected bgCenterX( w: number ): number
  {
    return LayoutScaler.xFromDesign( AbstractTournamentDialog.PAD_X + ( AbstractTournamentDialog.BG_W - w ) / 2 );
  }

  protected bgX( x: number ): number
  {
    return LayoutScaler.xFromDesign( AbstractTournamentDialog.PAD_X + x );
  }

  protected bgBottomY( y: number ): number
  {
    return this.bgY( 0 ) + this.bg_h - LayoutScaler.heightFromDesign( y );
  }

  protected bgY( y: number ): number
  {
    return LayoutScaler.yFromDesign( AbstractTournamentDialog.PAD_Y + y );
  }

  private createBackground(): void
  {
    this.addChild( new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( AbstractTournamentDialog.PAD_X ),
      this.bgY( 0 ),
      LayoutScaler.widthFromDesign( AbstractTournamentDialog.BG_W ),
      this.bg_h,
      ImgID.UI_TournamentBgBlue,
      "tournament_bg_blue"
    ) );
  }

  /** Size of the bg_part art in design px; a dialog overrides it for its own size. */
  protected partWidth(): number { return 966; }
  protected partHeight(): number { return this.partWidth() * 574 / 966; }

  private createBackgroundPart(): void
  {
    const part_w = this.partWidth();
    const part_h = this.partHeight();
    const x = this.bgCenterX( part_w );
    const y = this.bgY( AbstractTournamentDialog.PART_Y );
    const w = LayoutScaler.widthFromDesign( part_w );
    const h = LayoutScaler.heightFromDesign( part_h );
    this.addChild( new UIImage( this.ctx, x, y, w, h, ImgID.UI_TournamentBgPart ) );
  }

  private createTopSubstrate(): void
  {
    this.addChild( new UIImage(
      this.ctx,
      this.bgCenterX( AbstractTournamentDialog.TOP_SUBSTRATE_W ),
      this.bgY( AbstractTournamentDialog.TOP_SUBSTRATE_Y ),
      LayoutScaler.widthFromDesign( AbstractTournamentDialog.TOP_SUBSTRATE_W ),
      LayoutScaler.heightFromDesign( AbstractTournamentDialog.TOP_SUBSTRATE_H ),
      ImgID.UI_TournamentTopSubstrate
    ) );
  }

  private createTitle(): void
  {
    const pos = this.titlePos();

    for ( const layer of TournamentTitle.createTitleLayers( this.ctx, pos.x, pos.y ) )
      this.addChild( layer );
  }

  private createCloseButton(): void
  {
    const circle_x = AbstractTournamentDialog.BG_W + AbstractTournamentDialog.CORNER_BTN_OVERHANG_X - AbstractTournamentDialog.CORNER_BTN_SIZE;

    this.close_btn = this.addCornerButton(
      circle_x,
      ImgID.UI_SettingsDialogCloseBtn,
      AbstractTournamentDialog.CLOSE_ART_SIZE,
      AbstractTournamentDialog.CLOSE_ART_PAD
    );
    this.close_btn.setOnClick( () => this.close() );
  }

  protected addCornerButton( circle_x: number, img_id: ImgID, art_size: number, art_pad: number ): UIImage
  {
    const scale = AbstractTournamentDialog.CORNER_BTN_SIZE / ( art_size - art_pad * 2 );
    const size = art_size * scale;
    const pad = art_pad * scale;

    const button = new UIImage(
      this.ctx,
      this.bgX( circle_x - pad ),
      this.bgY( AbstractTournamentDialog.CORNER_BTN_Y - pad ),
      LayoutScaler.widthFromDesign( size ),
      LayoutScaler.heightFromDesign( size ),
      img_id,
      undefined,
      true
    );
    this.addChild( button );
    return button;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }
}
